// FormTestingModal — the builder's form-testing dialog, in two states driven by
// the form detail's `testing` flag (raw `isTestingMode`):
//
//   off -> "Start testing this form?"  success tile, Cancel + Start testing
//   on  -> "Form testing is on"        warning tile, Stop testing + Done
//
// One component with a branch rather than two files: both states share the flask
// tile, the note strip and the one mutation.
//
// Built on Modal, not ConfirmModal: ConfirmModal renders its `description` inside
// a <p>, so the note strip could not nest there, and its footer is hard-coded
// Cancel+Confirm — the "on" state needs Stop at the inline-start and Done at the
// inline-end (hence the .form-testing footer override in the stylesheet).

import { useTranslation, Trans } from 'react-i18next';
import toast from 'react-hot-toast';
import { Alert, Button, Modal } from '@/components/common';
import { useSetTestStatusMutation } from '@/api/endpoints/formsApi';

interface FormTestingModalProps {
  open: boolean;
  onClose: () => void;
  formId: string;
  /** Current server state — picks which of the two states renders. */
  testing: boolean;
}

const FormTestingModal = ({ open, onClose, formId, testing }: FormTestingModalProps) => {
  const { t } = useTranslation();
  const [setTestStatus, { isLoading }] = useSetTestStatusMutation();

  // The mutation invalidates the form's detail tag, so `testing` arrives back
  // from the refetch — this component never mirrors it locally.
  const run = async (markAsTest: boolean) => {
    try {
      await setTestStatus({ formIds: [formId], markAsTest }).unwrap();
      toast.success(t(markAsTest ? 'forms_builder_test_started' : 'forms_builder_test_stopped'));
      onClose();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size='md'
      className='form-testing'
      icon='flask-conical'
      tone={testing ? 'warning' : 'success'}
      title={t(testing ? 'forms_builder_test_on_title' : 'forms_builder_test_start_title')}
      footer={
        testing ? (
          <>
            <Button
              variant='danger'
              iconStart='square'
              loading={isLoading}
              onClick={() => run(false)}
            >
              {t('forms_builder_stop_testing')}
            </Button>
            <Button onClick={onClose} disabled={isLoading}>
              {t('button_done')}
            </Button>
          </>
        ) : (
          <>
            <Button variant='secondary' onClick={onClose} disabled={isLoading}>
              {t('button_cancel')}
            </Button>
            <Button iconStart='flask-conical' loading={isLoading} onClick={() => run(true)}>
              {t('forms_builder_start_testing')}
            </Button>
          </>
        )
      }
    >
      <p className='form-testing__body'>
        {testing ? (
          // Bold mid-sentence, so it goes through Trans (the
          // forms_builder_props_otp_message_hint idiom).
          <Trans i18nKey='forms_builder_test_on_desc'>
            View it on the mobile app via <strong>More &gt; Test form</strong>.
          </Trans>
        ) : (
          t('forms_builder_test_start_desc')
        )}
      </p>
      <Alert variant='neutral'>
        {t(testing ? 'forms_builder_test_on_note' : 'forms_builder_test_start_note')}
      </Alert>
    </Modal>
  );
};

export default FormTestingModal;
